<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { authService } from '../services/auth';
import { collaborationApi } from '../services/collaboration';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const mode = computed(() => String(route.meta.authMode || 'login'));
const title = computed(() => mode.value === 'register' ? 'Crear cuenta' : mode.value === 'recovery' ? 'Recuperar contraseña' : mode.value === 'reset' ? 'Nueva contraseña' : 'Iniciar sesión');
const message = ref('');
const error = ref('');
const busy = ref(false);
const form = ref({ identifier: '', username: '', email: '', name: '', password: '', currentPassword: '', token: '', newPassword: '' });
const devRecoveryToken = ref('');
const resetSent = ref(false);
const invitationId = computed(() => { const raw = route.query.invitacion ?? route.query.invitation; return typeof raw === 'string' ? raw.trim() : ''; });
const invitation = ref<{ id:string; email:string; permission:'read'|'edit'; caseId:string; caseTitle:string } | null>(null);

async function submit() {
  busy.value = true; message.value = ''; error.value = '';
  try {
    if (mode.value === 'login') {
      await auth.login(form.value.identifier, form.value.password);
      if (invitationId.value) {
        await collaborationApi.acceptInvitation(invitationId.value);
        await router.replace({ path: '/fase-33', query: { invited: '1' } });
        return;
      }
      await router.replace(String(route.query.redirect || '/'));
    } else if (mode.value === 'register') {
      await auth.register(form.value.username, form.value.email, form.value.password, form.value.name);
      if (invitationId.value) {
        await collaborationApi.acceptInvitation(invitationId.value);
        await router.replace({ path: '/fase-33', query: { invited: '1' } });
        return;
      }
      await router.replace('/');
    } else if (mode.value === 'recovery') {
      const result = await authService.forgotPassword(form.value.identifier);
      message.value = result.message;
      resetSent.value = Boolean(result.message);
      devRecoveryToken.value = result.recoveryToken || '';
      if (result.recoveryToken) {
        form.value.token = result.recoveryToken;
      }
    } else {
      await authService.resetPassword(form.value.token, form.value.password);
      message.value = 'Contraseña actualizada. Ya puedes iniciar sesión.';
      await router.replace('/login');
    }
  } catch (e) { error.value = e instanceof Error ? e.message : 'No se pudo completar la operación.'; }
  finally { busy.value = false; }
}

onMounted(async () => {
  await auth.bootstrap();
  const token = typeof route.query.token === 'string' ? route.query.token.trim() : '';
  if (mode.value === 'reset' && token) form.value.token = token;

  if (invitationId.value && (mode.value === 'register' || mode.value === 'login')) {
    try {
      const result = await collaborationApi.previewInvitation(invitationId.value);
      invitation.value = result.invitation;
      form.value.email = result.invitation.email;
      form.value.identifier = result.invitation.email;
      if (auth.authenticated) {
        await collaborationApi.acceptInvitation(invitationId.value);
        await router.replace({ path: '/fase-33', query: { invited: '1' } });
        return;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'La invitación no es válida o ya ha sido aceptada.';
    }
  }

  if (auth.authenticated && ['login','register'].includes(mode.value) && !invitationId.value) void router.replace('/');
});
</script>

<template>
  <main class="auth-page">
    <section class="auth-card">
      <p class="eyebrow">MINDPLACE · FASE 34</p>
      <h1>{{ title }}</h1>
      <p class="intro">Cuenta separada de tu espacio local. Tus casos locales siguen disponibles sin cuenta; los casos sincronizados solo llegan mediante tu sesión.</p>
      <form @submit.prevent="submit">
        <template v-if="mode==='login'">
          <label>Usuario o email<input v-model.trim="form.identifier" autocomplete="username" required placeholder="usuario o email@ejemplo.com"></label>
          <label>Contraseña<input v-model="form.password" type="password" autocomplete="current-password" required></label>
          <button class="primary" :disabled="busy">{{ busy ? 'Entrando…' : 'Iniciar sesión' }}</button>
          <div class="links"><RouterLink to="/registro">Crear cuenta</RouterLink><RouterLink to="/recuperar">He olvidado mi contraseña</RouterLink></div>
        </template>
        <template v-else-if="mode==='register'">
          <label>Usuario<input v-model.trim="form.username" autocomplete="username" required minlength="3" maxlength="32" pattern="[\-a-zA-Z0-9._]+" placeholder="marcos"></label>
          <div v-if="invitation" class="invite-notice"><strong>Invitación a MindPlace</strong><span>{{ invitation.caseTitle }} · {{ invitation.permission==='edit' ? 'Edición' : 'Lectura' }}</span><small>La cuenta debe crearse con {{ invitation.email }} para aceptar automáticamente esta invitación.</small></div>
          <label>Email<input v-model.trim="form.email" type="email" autocomplete="email" :readonly="Boolean(invitation)" required></label>
          <label>Nombre visible<input v-model.trim="form.name" autocomplete="name" maxlength="80" placeholder="Nombre"></label>
          <label>Contraseña<input v-model="form.password" type="password" autocomplete="new-password" minlength="8" maxlength="128" required></label>
          <button class="primary" :disabled="busy">{{ busy ? 'Creando…' : 'Crear cuenta' }}</button>
          <div class="links"><RouterLink :to="invitationId ? { path: '/login', query: { invitacion: invitationId } } : '/login'">Ya tengo cuenta</RouterLink></div>
        </template>
        <template v-else-if="mode==='recovery'">
          <label>Usuario o email<input v-model.trim="form.identifier" autocomplete="username" required placeholder="usuario o email@ejemplo.com"></label>
          <button class="primary" :disabled="busy">{{ busy ? 'Enviando…' : 'Solicitar recuperación' }}</button>
          <p v-if="resetSent" class="success">Revisa el correo de la cuenta. El enlace caducará en 30 minutos y solo funciona una vez.</p>
          <p v-if="devRecoveryToken" class="dev-token"><strong>Modo desarrollo:</strong> token generado para pruebas:<br><code>{{ devRecoveryToken }}</code></p>
          <div class="links"><RouterLink to="/login">Volver a iniciar sesión</RouterLink></div>
        </template>
        <template v-else>
          <label>Token de recuperación<textarea v-model.trim="form.token" rows="3" required placeholder="Se completa automáticamente al abrir el enlace del correo"></textarea></label>
          <label>Nueva contraseña<input v-model="form.password" type="password" autocomplete="new-password" minlength="8" maxlength="128" required></label>
          <button class="primary" :disabled="busy">{{ busy ? 'Actualizando…' : 'Cambiar contraseña' }}</button>
          <div class="links"><RouterLink to="/login">Volver a iniciar sesión</RouterLink></div>
        </template>
      </form>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <p v-if="message" class="success" role="status">{{ message }}</p>
      <button v-if="mode==='login'||mode==='register'||mode==='recovery'" class="local" type="button" @click="router.push('/')">Continuar sin cuenta</button>
    </section>
  </main>
</template>

<style scoped>
.auth-page{min-height:100dvh;display:grid;place-items:center;padding:24px;background:radial-gradient(circle at top,#15243b,#080d17 62%);color:#e8eef7;font:15px/1.45 system-ui,sans-serif}.auth-card{width:min(460px,100%);box-sizing:border-box;padding:28px;border:1px solid #2b3d56;background:rgba(15,24,40,.96);border-radius:22px;box-shadow:0 30px 90px rgba(0,0,0,.38)}.eyebrow{margin:0;font-size:11px;letter-spacing:.16em;color:#8fa2bb;font-weight:800}.auth-card h1{margin:8px 0;font-size:34px}.intro{color:#99a8bc}.auth-card form{display:grid;gap:13px;margin-top:20px}.auth-card label{display:grid;gap:6px;color:#c5cfdd}.auth-card input,.auth-card textarea{box-sizing:border-box;width:100%;border:1px solid #344861;background:#0f1828;color:#f3f6fa;border-radius:10px;padding:11px 12px;font:inherit}.auth-card input:focus,.auth-card textarea:focus{outline:2px solid #6f8fb7;outline-offset:1px}.primary,.local{border-radius:10px;padding:11px 14px;font:700 14px/1.1 system-ui,sans-serif;cursor:pointer}.primary{border:1px solid #607da6;background:#29415f;color:#fff}.primary:disabled{opacity:.55;cursor:not-allowed}.local{margin-top:14px;width:100%;border:1px solid #33465e;background:transparent;color:#b8c5d5}.links{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap}.links a{color:#adc2df;text-decoration:none}.error,.success,.dev-token{padding:10px 12px;border-radius:10px}.error{color:#ffb7b7;background:#341d24;border:1px solid #6a3945}.success{color:#a0e6c5;background:#183229;border:1px solid #31614f}.dev-token{color:#f2dca8;background:#382f1d;border:1px solid #685733}.dev-token code{word-break:break-all}.invite-notice{display:grid;gap:3px;padding:12px;border:1px solid #3b5b7d;background:#112237;border-radius:10px}.invite-notice span{font-weight:700;color:#dce9f8}.invite-notice small{color:#9fb1c7}
</style>
